import type { RoomKind } from './Room';

/**
 * #4888 — what a room is FOR (epic #4887, `_docs/Q4/ExperienceFirst/README.md`).
 *
 * The host picks this out loud when creating the room, and the room opens into it. That is the
 * difference from the experience lanes removed on 2026-08-14: they reordered the panel by a mode
 * the host had usually never chosen. A room's purpose is stored on `rooms.experience`; the engine
 * it runs on is still `room_type`, decided by one question — will this be recorded or streamed?
 */
export const ROOM_EXPERIENCE_IDS = ['game_day', 'game_night', 'hangout', 'show', 'learn'] as const;
export type RoomExperienceId = typeof ROOM_EXPERIENCE_IDS[number];

/** The one extra question a card asks at creation. */
export type RoomExperienceQuestion = 'team' | 'game' | 'vibe' | 'show_title' | 'subject';

/** What the room opens into when the host arrives and nothing is running yet. */
export type RoomExperienceStart =
  | { kind: 'none' }
  | { kind: 'mode'; modeId: string }
  /**
   * The room's first game starts on arrival. `setup` is that game's starting settings by module id,
   * so a host on their own can play straight away (Hold'em seats one AI opponent).
   */
  | { kind: 'activity'; setup?: Record<string, Record<string, unknown>> };

/**
 * #4891 — what the simple host panel shows for an experience: its own controls (`main`), then the
 * background extras that sit beside them. Everything else is one Switch away, never on screen.
 */
export type RoomExperienceSection = 'game_mode' | 'games' | 'web_games' | 'watch_together' | 'ask_tele';
export type RoomExperienceExtra = 'music' | 'odds' | 'screen_share';

export interface RoomExperiencePanel {
  main: readonly RoomExperienceSection[];
  extras: readonly RoomExperienceExtra[];
  /** Room modes the simple experience may run (Game day: College Football; Hang out: GenTV). */
  modeIds: readonly string[];
}

export interface RoomExperienceVibe {
  id: string;
  label: string;
  /** Written into the companion template's `personality` blank. */
  personality: string;
}

export interface RoomExperienceDefinition {
  id: RoomExperienceId;
  label: string;
  /** One line under the label on the create card. */
  line: string;
  emoji: string;
  /** Record/stream or not: `townhall` is the only engine that records or streams. */
  engine: Extract<RoomKind, 'standard' | 'townhall'>;
  /** Billing tier key in `ROOM_BILLING_POLICY_B64` the card's rate is read from. */
  billingRoomType: Extract<RoomKind, 'standard' | 'townhall'>;
  /** Mode surcharges a card mentions next to its rate. */
  surchargeModeIds: readonly string[];
  question: RoomExperienceQuestion;
  questionLabel: string;
  questionPlaceholder?: string;
  /** Pre-filled room name, before sanitising. */
  defaultName: string;
  /** `@telegamez/types` CompanionTemplates id the room's companion starts on. */
  companionTemplateId: string;
  /** Default for the companion's picture-in-picture tile (#4315). Null keeps the room kind's default. */
  companionTileHidden: boolean | null;
  start: RoomExperienceStart;
  /** The simple host panel's contents; null runs the full panel (Make a show, Learn). */
  panel: RoomExperiencePanel | null;
  /** False hides the card until the experience is built. */
  available: boolean;
}

export const ROOM_EXPERIENCE_VIBES: readonly RoomExperienceVibe[] = [
  { id: 'laid_back', label: 'Laid-back', personality: 'relaxed, warm, easygoing' },
  { id: 'banter', label: 'Banter', personality: 'quick, teasing, quick to a comeback' },
  { id: 'game_show', label: 'Game-show energy', personality: 'big, upbeat, game-show host energy' },
];

export const DEFAULT_ROOM_EXPERIENCE_VIBE_ID = 'laid_back';

export const ROOM_EXPERIENCE_DEFINITIONS: readonly RoomExperienceDefinition[] = [
  {
    id: 'game_day', label: 'Game day', line: 'Your crew, the game, an AI sports junkie', emoji: '🏈',
    engine: 'standard', billingRoomType: 'standard', surchargeModeIds: ['cfb'],
    question: 'team', questionLabel: 'Which team or sport?', questionPlaceholder: 'Like Ohio State or the Big Ten',
    defaultName: 'game-day',
    companionTemplateId: 'sports_caster', companionTileHidden: false,
    start: { kind: 'mode', modeId: 'cfb' },
    panel: { main: ['game_mode', 'watch_together'], extras: ['odds', 'screen_share', 'music'], modeIds: ['cfb'] },
    available: true,
  },
  {
    id: 'game_night', label: 'Game night', line: 'Poker, racing, party games, web games', emoji: '🎲',
    engine: 'standard', billingRoomType: 'standard', surchargeModeIds: [],
    question: 'game', questionLabel: 'Which game first?',
    defaultName: 'game-night',
    companionTemplateId: 'quiz_host', companionTileHidden: true,
    start: { kind: 'activity', setup: { holdem: { aiPlayers: 1 }, pallets: { aiPlayers: 1 } } },
    // Play Together is the screen share a gamer starts; odds stay out of a poker table or a race.
    panel: { main: ['games', 'web_games'], extras: ['screen_share', 'music'], modeIds: [] },
    available: true,
  },
  {
    id: 'hangout', label: 'Hang out', line: 'Watch YouTube, play music, ask Tele anything', emoji: '☕',
    engine: 'standard', billingRoomType: 'standard', surchargeModeIds: [],
    question: 'vibe', questionLabel: 'What vibe?',
    defaultName: 'hangout',
    companionTemplateId: 'helpful_assistant', companionTileHidden: false,
    start: { kind: 'none' },
    // Keep GenTV with the watch-together tools: Hang out rooms can host a story
    // alongside YouTube and music without changing their standard-room engine.
    panel: { main: ['watch_together', 'game_mode', 'ask_tele'], extras: ['music', 'odds', 'screen_share'], modeIds: ['gentv'] },
    available: true,
  },
  {
    id: 'show', label: 'Make a show', line: 'Record it, stream it, bring an audience', emoji: '🎙',
    engine: 'townhall', billingRoomType: 'townhall', surchargeModeIds: [],
    question: 'show_title', questionLabel: 'What’s the show called?', questionPlaceholder: 'Like Friday Film Club',
    defaultName: 'the-show',
    companionTemplateId: 'show_producer', companionTileHidden: null,
    start: { kind: 'none' }, panel: null, available: true,
  },
  {
    // EducationMode (#4770): shown once the Education engine ships.
    id: 'learn', label: 'Learn', line: 'Study together with Tele: quizzes, challenges, progress', emoji: '📚',
    engine: 'standard', billingRoomType: 'standard', surchargeModeIds: [],
    question: 'subject', questionLabel: 'What subject?',
    defaultName: 'study-group',
    companionTemplateId: 'tutor', companionTileHidden: false,
    start: { kind: 'none' }, panel: null, available: false,
  },
];

export function isRoomExperienceId(value: unknown): value is RoomExperienceId {
  return typeof value === 'string' && (ROOM_EXPERIENCE_IDS as readonly string[]).includes(value);
}

export function getRoomExperience(id: unknown): RoomExperienceDefinition | null {
  return ROOM_EXPERIENCE_DEFINITIONS.find(experience => experience.id === id) ?? null;
}

export function getAvailableRoomExperiences(): RoomExperienceDefinition[] {
  return ROOM_EXPERIENCE_DEFINITIONS.filter(experience => experience.available);
}

/**
 * The purpose a room is treated as when `rooms.experience` is NULL (rooms created before #4888 and
 * not yet backfilled): a townhall is a show, everything else hangs out.
 */
export function roomExperienceForRoom(room: { experience?: string | null; room_type?: string | null; roomType?: string | null } | null | undefined): RoomExperienceId {
  if (isRoomExperienceId(room?.experience)) return room.experience;
  return (room?.room_type ?? room?.roomType) === 'townhall' ? 'show' : 'hangout';
}

/**
 * #4892 — whether a room's engine records and streams. Only Make a show (townhall) does; talk
 * shows are admin-provisioned with their own runtime and keep what they had. Server checks at the
 * outward outputs (stream destinations, recording download and status) go through this.
 */
export function roomEngineRecordsAndStreams(roomType: string | null | undefined): boolean {
  return roomType === 'townhall' || roomType === 'talk_show';
}

export const ROOM_EXPERIENCE_ANSWER_MAX_LENGTH = 40;

/**
 * The room's purpose details, stored under `workflows.active_configuration -> 'experience'`, beside
 * `music` and `companionPip`. `activityModuleId` is Game night's first game; `answer` is the
 * free-text team, show title or subject; `vibeId` is Hang out's vibe.
 */
export interface RoomExperienceSetup {
  id: RoomExperienceId;
  activityModuleId?: string | null;
  answer?: string | null;
  vibeId?: string | null;
}

export function readRoomExperienceSetup(value: unknown): RoomExperienceSetup | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (!isRoomExperienceId(raw.id)) return null;
  const text = (input: unknown) => typeof input === 'string' && input.trim()
    ? input.trim().slice(0, ROOM_EXPERIENCE_ANSWER_MAX_LENGTH) : null;
  return {
    id: raw.id,
    activityModuleId: text(raw.activityModuleId),
    answer: text(raw.answer),
    vibeId: ROOM_EXPERIENCE_VIBES.some(vibe => vibe.id === raw.vibeId) ? raw.vibeId as string : null,
  };
}

/** The companion template blanks a purpose starts the room's companion with. */
export function roomExperienceCompanionBlanks(setup: RoomExperienceSetup): { personality?: string; subject?: string } {
  const experience = getRoomExperience(setup.id);
  if (!experience) return {};
  if (experience.question === 'vibe') {
    const vibe = ROOM_EXPERIENCE_VIBES.find(entry => entry.id === (setup.vibeId ?? DEFAULT_ROOM_EXPERIENCE_VIBE_ID));
    return vibe ? { personality: vibe.personality } : {};
  }
  if (experience.question === 'game') return {};
  return setup.answer ? { subject: setup.answer } : {};
}
